import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, reactive, ref, watch } from 'vue';
import {
    setWilcomCredentials,
    setMelcoCredentials,
    setOpenAiApiKey,
    setRemoveBgApiKey,
    setCutoutProApiKey,
    setRemoveBgFallback,
    setCutoutProFallback,
    setSpotifyCredentials,
    deleteCanvaCredentials,
    setGoogleGeminiApiKey,
    setOpenRouterApiKey,
    setPicWishApiKey,
    getRateLimits,
    type RateLimitDto
} from '@/api/user';
import { CustomilyUser } from '@/typings/user';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';
import ApiKeyInput from '@/components/ApiKeyInput/ApiKeyInput.vue';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import ApiRateLimitModal from '@/views/Account/components/ApiRateLimitModal.vue';
import store from '@/store';
import { AxiosError } from 'axios';


export default /*#__PURE__*/_defineComponent({
  __name: 'Integrations',
  emits: ["user-updated"],
  setup(__props, { emit }: { emit: ({
    (event: 'user-updated'): void;
}), expose: any, slots: any, attrs: any }) {



const isImageEffectsEnabled = process.env.VUE_APP_IMAGE_EFFECTS_ENABLED === 'true';

const removeBgApiKey = ref('');
const wilcomAppId = ref('');
const wilcomAppKey = ref('');
const melcoUsername = ref('');
const melcoApiKey = ref('');
const cutoutProApiKey = ref('');
const cutoutProError = ref('');
const removeBgFallback = ref(false);
const cutoutProFallback = ref(false);
const googleGeminiError = ref('');
const openRouterError = ref('');

const openAiApiKey = ref('');
const openAiApiKeyState = ref<boolean | null>(null);
const openAiError = ref('');

const spotifyClientID = ref('');
const spotifyClientSecret = ref('');

const googleGeminiApiKey = ref('');
const openRouterApiKey = ref('');

const picWishApiKey = ref('');
const picWishError = ref('');

const rateLimits = ref<Record<string, RateLimitDto>>({});
const showRateLimitModal = ref(false);
const activeRateLimit = ref<RateLimitDto & { label: string }>({
    provider: '',
    label: '',
    attemptsPerIp: null,
    retryAfterMinutes: null
});

const openRateLimitModal = (provider: string, label: string) => {
    const existing = rateLimits.value[provider];
    activeRateLimit.value = {
        provider,
        label,
        attemptsPerIp: existing?.attemptsPerIp ?? null,
        retryAfterMinutes: existing?.retryAfterMinutes ?? null
    };
    showRateLimitModal.value = true;
};

const onRateLimitSaved = async () => {
    try {
        const list = await getRateLimits();
        rateLimits.value = Object.fromEntries(list.map((r) => [r.provider, r]));
    } catch {
        // ignore — modal already closed, stale values are acceptable
    }
};

const showFields = reactive({
    removeBg: false,
    openAi: false,
    spotifyClientId: false,
    spotifyClientSecret: false,
    cutoutPro: false,
    wilcomAppId: false,
    wilcomAppKey: false,
    melcoUsername: false,
    melcoApiKey: false,
    googleGemini: false,
    openRouter: false,
    picWish: false
});

const savingState = reactive({
    removeBg: false,
    removeBgFallback: false,
    cutoutProFallback: false,
    wilcomCredentials: false,
    melcoCredentials: false,
    openAi: false,
    cutoutPro: false,
    spotifyCredentials: false,
    canvaCredentials: false,
    googleGemini: false,
    openRouter: false,
    picWish: false
});

const user = computed((): CustomilyUser | null | undefined => store.state.user);
const openAiVerified = computed(() => {
    if (!openAiApiKey.value) {
        return true;
    }
    return (
        typeof openAiApiKey.value === 'string' && openAiApiKey.value.length > 0 && openAiApiKey.value.startsWith('sk')
    );
});

const modifiedRemoveBgApiKey = computed(() => user.value?.RemoveBgAPIKey != removeBgApiKey.value);
const modifiedCutoutProApiKey = computed(() => user.value?.CutoutproAPIKey != cutoutProApiKey.value);

// user can still be null on mount (e.g. hard-refresh on this route, before the store's async user fetch resolves) —
// re-populate whenever it (re)arrives instead of a one-shot onBeforeMount read.
watch(
    user,
    (currentUser) => {
        removeBgApiKey.value = currentUser?.RemoveBgAPIKey || '';
        openAiApiKey.value = currentUser?.OpenAiAPIKey || '';
        melcoUsername.value = currentUser?.MelcoUsername || '';
        melcoApiKey.value = currentUser?.MelcoApiKey || '';
        wilcomAppId.value = currentUser?.WilcomAppId || '';
        wilcomAppKey.value = currentUser?.WilcomAppKey || '';
        cutoutProApiKey.value = currentUser?.CutoutproAPIKey || '';
        spotifyClientID.value = currentUser?.SpotifyClientID || '';
        spotifyClientSecret.value = currentUser?.SpotifyClientSecret || '';
        googleGeminiApiKey.value = currentUser?.GoogleGeminiAPIKey || '';
        openRouterApiKey.value = currentUser?.OpenRouterAPIKey || '';
        picWishApiKey.value = currentUser?.PicWishAPIKey || '';
        removeBgFallback.value = currentUser?.RemoveBgFallback === true;
        cutoutProFallback.value = currentUser?.CutoutProFallback === true;
    },
    { immediate: true }
);

onBeforeMount(async () => {
    try {
        const list = await getRateLimits();
        rateLimits.value = Object.fromEntries(list.map((r) => [r.provider, r]));
    } catch {
        // non-critical — rate limit buttons still open the modal, just without pre-filled values
    }
});

const onRemoveBgSubmit = async () => {
    try {
        savingState.removeBg = true;
        await setRemoveBgApiKey(removeBgApiKey.value);
    } finally {
        savingState.removeBg = false;
        emit('user-updated');
    }
};

const onOpenAiSubmit = async () => {
    savingState.openAi = true;
    try {
        await setOpenAiApiKey(openAiApiKey.value);
        openAiApiKeyState.value = true;
        openAiError.value = '';
        emit('user-updated');
    } catch (error) {
        openAiApiKeyState.value = false;
        openAiError.value = (error as Error).message;
    } finally {
        savingState.openAi = false;
    }
};

const onWilcomCredentialsSubmit = async () => {
    try {
        savingState.wilcomCredentials = true;
        await setWilcomCredentials(wilcomAppId.value, wilcomAppKey.value);
    } finally {
        savingState.wilcomCredentials = false;
        emit('user-updated');
    }
};

const onMelcoCredentialsSubmit = async () => {
    try {
        savingState.melcoCredentials = true;
        await setMelcoCredentials(melcoUsername.value, melcoApiKey.value);
    } finally {
        savingState.melcoCredentials = false;
        emit('user-updated');
    }
};

const onCutoutProSubmit = async () => {
    try {
        savingState.cutoutPro = true;
        cutoutProError.value = '';
        await setCutoutProApiKey(cutoutProApiKey.value);
        emit('user-updated');
    } catch (error) {
        cutoutProError.value = (error as AxiosError).response?.data?.Message;
    } finally {
        savingState.cutoutPro = false;
    }
};

const onRemoveBgFallbackChange = async (enabled: boolean) => {
    try {
        savingState.removeBgFallback = true;
        await setRemoveBgFallback(enabled);
        emit('user-updated');
    } catch {
        removeBgFallback.value = !enabled; // revert the switch, the setting wasn't saved
    } finally {
        savingState.removeBgFallback = false;
    }
};

const onCutoutProFallbackChange = async (enabled: boolean) => {
    try {
        savingState.cutoutProFallback = true;
        await setCutoutProFallback(enabled);
        emit('user-updated');
    } catch {
        cutoutProFallback.value = !enabled; // revert the switch, the setting wasn't saved
    } finally {
        savingState.cutoutProFallback = false;
    }
};

const onSpotifyCredentialsSubmit = async () => {
    try {
        savingState.spotifyCredentials = true;
        await setSpotifyCredentials(spotifyClientID.value, spotifyClientSecret.value);
    } finally {
        savingState.spotifyCredentials = false;
        emit('user-updated');
    }
};

const diconnectUserFromCanva = async () => {
    try {
        savingState.canvaCredentials = true;
        await deleteCanvaCredentials();
    } finally {
        savingState.canvaCredentials = false;
        emit('user-updated');
    }
};

const onGoogleGeminiApiSubmit = async () => {
    try {
        savingState.googleGemini = true;
        googleGeminiError.value = '';
        await setGoogleGeminiApiKey(googleGeminiApiKey.value);
    } catch (error) {
        googleGeminiError.value = (error as Error).message;
    } finally {
        savingState.googleGemini = false;
        emit('user-updated');
    }
};

const onOpenRouterApiSubmit = async () => {
    try {
        savingState.openRouter = true;
        openRouterError.value = '';
        await setOpenRouterApiKey(openRouterApiKey.value);
    } catch (error) {
        openRouterError.value = (error as Error).message;
    } finally {
        savingState.openRouter = false;
        emit('user-updated');
    }
};
const onPicWishApiSubmit = async () => {
    try {
        savingState.picWish = true;
        picWishError.value = '';
        await setPicWishApiKey(picWishApiKey.value);
    } catch (error) {
        picWishError.value = (error as Error).message;
    } finally {
        savingState.picWish = false;
        emit('user-updated');
    }
};

return { __sfc: true,emit, isImageEffectsEnabled, removeBgApiKey, wilcomAppId, wilcomAppKey, melcoUsername, melcoApiKey, cutoutProApiKey, cutoutProError, removeBgFallback, cutoutProFallback, googleGeminiError, openRouterError, openAiApiKey, openAiApiKeyState, openAiError, spotifyClientID, spotifyClientSecret, googleGeminiApiKey, openRouterApiKey, picWishApiKey, picWishError, rateLimits, showRateLimitModal, activeRateLimit, openRateLimitModal, onRateLimitSaved, showFields, savingState, user, openAiVerified, modifiedRemoveBgApiKey, modifiedCutoutProApiKey, onRemoveBgSubmit, onOpenAiSubmit, onWilcomCredentialsSubmit, onMelcoCredentialsSubmit, onCutoutProSubmit, onRemoveBgFallbackChange, onCutoutProFallbackChange, onSpotifyCredentialsSubmit, diconnectUserFromCanva, onGoogleGeminiApiSubmit, onOpenRouterApiSubmit, onPicWishApiSubmit, LoaderBtn, ApiKeyInput, CCheckbox, ApiRateLimitModal }
}

})